"use client";
import { PlusIcon, DollarSign, CalendarIcon, ChevronsUpDown, Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

import {
    Dialog,
    ResponsiveDialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import {
    CardContent,
    CardHeader,
    CardTitle,
} from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/app/_module/components/ui/command";
import { CurrencyInput } from "@/app/_module/components/CurrencyInput";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { cn } from "@/app/_module/lib/utils";

import { useLeadWithPolicies, useUpdatePolicy, useGetAgencyCarriers } from "../hooks/queries";
import type { leadsService } from "../services/services";
import type { Policy as LeadPolicy } from "../types/lead-with-policies-type";

import CreateLeadPolicy from "./CreateLeadPolicy";
import { LeadPolicyDetails } from "./LeadPolicyDetails";

type UpdatePolicyPayload = Parameters<
    typeof leadsService.updatePolicy
>[1];

interface UpdateLeadPolicyProps {
    leadId?: string;
}

const toDateInput = (value?: string | null) => {
    if (!value) return "";
    try {
        return value.slice(0, 10);
    } catch {
        return "";
    }
};

// Helper to calculate expiry date (1 year from issued date)
const calculateExpiryFromIssued = (issuedDateStr: string): string => {
    if (!issuedDateStr) return "";
    const [year, month, day] = issuedDateStr.split('-').map(Number);
    if (!year || !month || !day) return "";
    const expiryYear = year + 1;
    return `${expiryYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Wrapper component that accepts only leadId and manages dialog state
const UpdateLeadPolicy = function ({ leadId }: UpdateLeadPolicyProps) {
    const [open, setOpen] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState<LeadPolicy | null>(null);
    const [showPolicyModal, setShowPolicyModal] = useState(false);

    const { data, refetch } = useLeadWithPolicies(leadId || "");

    const policies = data?.data?.policies ?? [];
    const hasPolicies = policies.length > 0;
    const policyCount = policies.length;

    const openCreatePolicy = () => {
        setShowPolicyModal(true);
    };

    if (!leadId) {
        return null;
    }

    const handlePolicySave = (success: boolean) => {
        if (success) {
            // Policy was created successfully, refetch the data
            refetch();
        } else {
            // Policy creation failed, don't change status
        }
    };

    const handlePolicyUpdate = (success: boolean) => {
        if (success) {
            // Policy was updated successfully, refetch the data
            refetch();
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <CardHeader>
                    <div className="flex justify-between items-center mb-4">
                        <CardTitle className="text-base" />
                        <div className="flex gap-2 justify-end">
                            <Button onClick={openCreatePolicy}>
                              <PlusIcon className="h-4 w-4" />  New Policy
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                  <CardContent className="space-y-3 border-none">
                      <LeadPolicyDetails
                          hasPolicies={hasPolicies}
                          policies={policies}
                          policyCount={policyCount}
                          leadId={leadId}
                          leadName={data?.data?.name}
                          onPolicyUpdate={() => refetch()}
                      />
                  </CardContent>
            </div>

            <UpdatePolicyDialog
                isOpen={open}
                leadId={leadId}
                onOpenChange={setOpen}
                onSave={handlePolicyUpdate}
                policy={selectedPolicy}
            />

            {/* Policy Modal */}
            <CreateLeadPolicy
                isOpen={showPolicyModal}
                leadId={leadId}
                leadName={data?.data?.name}
                onOpenChange={setShowPolicyModal}
                onSave={handlePolicySave}
            />
        </div>
    );
};

// Internal dialog for editing a single policy
export const UpdatePolicyDialog = function ({
    isOpen,
    onOpenChange,
    onSave,
    leadId,
    policy,
    onStatusChange,
    newStatus,
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSave?: (success: boolean) => void;
    leadId: string;
    policy: LeadPolicy | null;
    onStatusChange?: (leadId: string, newStatus: string) => Promise<void>;
    newStatus?: string;
}) {
    const { user } = useUserContext();
    const agencyId = user?.currentAgency?.agency?.id;
    const { data: agencyCarriers = [], isLoading: isCarriersLoading } = useGetAgencyCarriers(agencyId);

    const initialState: UpdatePolicyPayload = useMemo(
        () => {
            const issuedAt = toDateInput(policy?.issuedAt);
            const existingExpiry = toDateInput(policy?.expiryDate ?? null);
            // If no expiry date but we have an issued date, calculate 1 year from issued
            const expiryDate = existingExpiry || calculateExpiryFromIssued(issuedAt);

            // Determine policyStage and status based on newStatus prop
            let policyStage = (policy as any)?.policyStage ?? "underwriting";
            let status = policy?.status ?? "";

            if (newStatus) {
                if (newStatus === "Sold & Approved") {
                    policyStage = "sold";
                    status = "active";
                } else if (newStatus === "Underwriting") {
                    policyStage = "underwriting";
                    status = "pending";
                } else if (newStatus === "Lapsed") {
                    policyStage = "sold";
                    status = "lapsed";
                } else if (newStatus === "Declined") {
                    policyStage = "underwriting";
                    status = "declined";
                }
            }

            return {
                leadId,
                policyNumber: policy?.policyNumber ?? "",
                productType: policy?.productType ?? "",
                status,
                policyStage,
                premium: policy?.premium ? Number(policy.premium) : 0,
                carrierName: policy?.carrierName ?? "",
                annualPremium: policy?.annualPremium ?? "",
                issuedAt,
                expiryDate,
            };
        },
        [leadId, policy, newStatus]
    );

    const [form, setForm] = useState<UpdatePolicyPayload>(initialState);
    const [carrierOpen, setCarrierOpen] = useState(false);
    // Native shell: inline overlay list instead of the popover (positioned
    // poppers paint offset from their trigger in the WKWebView).
    const [carrierSearch, setCarrierSearch] = useState("");

    useEffect(() => {
        setForm(initialState);
    }, [initialState, isOpen, newStatus]);

    const { mutateAsync, isPending } = useUpdatePolicy();

    // Product type options
    const ProductTypeOptions = [
        { value: "Whole Life Insurance", label: "Whole Life Insurance" },
        { value: "Term Life Insurance", label: "Term Life Insurance" },
        { value: "IUL", label: "IUL" },
    ] as const;

    const handleChange =
          (field: keyof UpdatePolicyPayload) =>
              (e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (field === "premium") {
                      setForm((prev: UpdatePolicyPayload) => ({ ...prev, premium: Number(value || 0) }));
                  } else if (field === "issuedAt") {
                      // When issued date changes, auto-calculate expiry date (1 year later)
                      const expiryDate = calculateExpiryFromIssued(value);
                      setForm((prev: UpdatePolicyPayload) => ({ ...prev, issuedAt: value, expiryDate }));
                  } else {
                      setForm((prev: UpdatePolicyPayload) => ({ ...prev, [field]: value }));
                  }
              };

    const handleClose = () => {
        onOpenChange(false);
    };

const handleSave = async () => {
    if (!policy?.id) {
        return;
    }
    try {
        // Prepare the data with proper formatting
        const formattedData = {
            ...form,
            leadId,
            // Convert annualPremium from string to number, removing any commas
            annualPremium: form.annualPremium
                ? Number(String(form.annualPremium).replace(/,/g, ''))
                : 0,
            // Send date strings as-is (YYYY-MM-DD format) - backend normalizes to noon UTC
            // This prevents timezone shift issues when dates are displayed back to the user
            expiryDate: form.expiryDate || null,
            issuedAt: form.issuedAt || null,
        };

        await mutateAsync({
            policyId: policy.id,
            data: formattedData,
        });

        // If policy status was changed to "lapsed", update the lead status to "Lapsed"
        if (form.status === 'lapsed' && onStatusChange) {
            await onStatusChange(leadId, "Lapsed");
        }
        // If policy status was changed to "declined", update the lead status to "Declined"
        else if (form.status === 'declined' && onStatusChange) {
            await onStatusChange(leadId, "Declined");
        }
        // If policy stage is "underwriting" (and status is not lapsed/declined), update the lead status to "Underwriting"
        else if (form.policyStage === 'underwriting' && onStatusChange) {
            await onStatusChange(leadId, "Underwriting");
        }
        // If policy stage was changed to "sold" (and status is not lapsed/declined), update the lead status to "Sold & Approved"
        else if (form.policyStage === 'sold' && onStatusChange) {
            await onStatusChange(leadId, "Sold & Approved");
        }

        onSave?.(true);
        onOpenChange(false);
    } catch (error) {
        onSave?.(false);
    }
};

    return (
        <Dialog onOpenChange={onOpenChange} open={isOpen}>
            <ResponsiveDialogContent size="2xl" className="p-0 border-0 shadow-lg">
                <DialogHeader className="p-6 pb-4">
                    <DialogTitle className="text-lg font-semibold text-gray-900">
                        Update Policy
                    </DialogTitle>
                    <DialogDescription>
                        Update the policy details for this lead
                    </DialogDescription>
                </DialogHeader>

                <div className="px-6 pb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="policyNumber">
                                Policy Number
                            </Label>
                            <Input
                                id="policyNumber"
                                onChange={handleChange("policyNumber")}
                                value={form.policyNumber}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="productType">
                                Product Type
                            </Label>
                            <Select
                                value={form.productType}
                                onValueChange={(value) =>
                                    setForm((prev: UpdatePolicyPayload) => ({ ...prev, productType: value }))
                                }
                            >
                                <SelectTrigger id="productType">
                                    <SelectValue placeholder="Select product type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ProductTypeOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="premium">
                                Coverage Amount ($)
                            </Label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <CurrencyInput
                                    id="premium"
                                    value={String(form.premium ?? 0)}
                                    onChange={(value) => setForm((prev: UpdatePolicyPayload) => ({ ...prev, premium: parseFloat(value) || 0 }))}
                                    placeholder="xx"
                                    showIcon={false}
                                    containerClass=""
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="carrierName">
                                Carrier Name
                            </Label>
                            {isNativePlatform() ? (
                            <div className="relative">
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={carrierOpen}
                                    className="w-full justify-between font-normal"
                                    type="button"
                                    onClick={() => { setCarrierOpen(!carrierOpen); setCarrierSearch(""); }}
                                >
                                    <span className="min-w-0 truncate text-left">
                                        {form.carrierName || (isCarriersLoading ? "Loading carriers..." : "Select carrier")}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                                {carrierOpen && (
                                    <div className="absolute right-0 top-full z-30 mt-1.5 w-[calc(200%+1rem)] overflow-hidden rounded-md border bg-white shadow-xl">
                                        <Input
                                            placeholder="Search carriers..."
                                            value={carrierSearch}
                                            onChange={(e) => setCarrierSearch(e.target.value)}
                                            className="rounded-none border-0 border-b focus-visible:ring-0"
                                        />
                                        <div className="max-h-[200px] overflow-y-auto overscroll-contain">
                                            {agencyCarriers
                                                .filter((c: any) => !carrierSearch || (c.carrierName || "").toLowerCase().includes(carrierSearch.toLowerCase()))
                                                .map((carrier: any, index: number) => (
                                                    <button
                                                        key={carrier.id || `carrier-${index}`}
                                                        type="button"
                                                        onClick={() => {
                                                            setForm((prev: UpdatePolicyPayload) => ({ ...prev, carrierName: carrier.carrierName }));
                                                            setCarrierOpen(false);
                                                        }}
                                                        className="flex w-full items-center px-3 py-2.5 text-left text-sm active:bg-gray-100"
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4 shrink-0",
                                                                form.carrierName === carrier.carrierName ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <span className="min-w-0 truncate">{carrier.carrierName}</span>
                                                    </button>
                                                ))}
                                            {agencyCarriers.filter((c: any) => !carrierSearch || (c.carrierName || "").toLowerCase().includes(carrierSearch.toLowerCase())).length === 0 && (
                                                <p className="px-3 py-3 text-center text-sm text-gray-500">No carriers found.</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            ) : (
                            <Popover open={carrierOpen} onOpenChange={setCarrierOpen} modal>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={carrierOpen}
                                        className="w-full justify-between font-normal"
                                    >
                                        {form.carrierName || (isCarriersLoading ? "Loading carriers..." : "Select carrier")}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                    side="bottom"
                                    onWheel={(e) => e.stopPropagation()}
                                >
                                    <Command>
                                        <CommandInput placeholder="Search carriers..." />
                                        <CommandList className="max-h-[200px]">
                                            <CommandEmpty>No carriers found.</CommandEmpty>
                                            <CommandGroup>
                                                {agencyCarriers.map((carrier: any, index: number) => (
                                                    <CommandItem
                                                        key={carrier.id || `carrier-${index}`}
                                                        value={carrier.carrierName}
                                                        onSelect={() => {
                                                            setForm((prev: UpdatePolicyPayload) => ({ ...prev, carrierName: carrier.carrierName }));
                                                            setCarrierOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                form.carrierName === carrier.carrierName ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {carrier.carrierName}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="annualPremium">
                                Annual Premium ($)
                            </Label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <CurrencyInput
                                    id="annualPremium"
                                    value={form.annualPremium}
                                    onChange={(value) => setForm((prev: UpdatePolicyPayload) => ({ ...prev, annualPremium: value }))}
                                    placeholder="xx"
                                    showIcon={false}
                                    containerClass=""
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="issuedAt">
                                Issued At
                            </Label>
                            <Popover>
                                <div className="relative">
                                    <Input
                                        id="issuedAt"
                                        type="date"
                                        value={form.issuedAt ?? ""}
                                        onChange={handleChange("issuedAt")}
                                        className="w-full pr-10"
                                    />
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                        >
                                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                                        </Button>
                                    </PopoverTrigger>
                                </div>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={form.issuedAt ? new Date(form.issuedAt + 'T12:00:00') : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                const year = date.getFullYear();
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const issuedAt = `${year}-${month}-${day}`;
                                                // Auto-calculate expiry date (1 year later)
                                                const expiryDate = `${year + 1}-${month}-${day}`;
                                                setForm((prev: UpdatePolicyPayload) => ({
                                                    ...prev,
                                                    issuedAt,
                                                    expiryDate
                                                }));
                                            }
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs" htmlFor="expiryDate">
                                Renewal Date
                            </Label>
                            <Popover>
                                <div className="relative">
                                    <Input
                                        id="expiryDate"
                                        type="date"
                                        value={form.expiryDate ?? ""}
                                        onChange={handleChange("expiryDate")}
                                        className="w-full pr-10"
                                    />
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                        >
                                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                                        </Button>
                                    </PopoverTrigger>
                                </div>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={form.expiryDate ? new Date(form.expiryDate + 'T12:00:00') : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                const year = date.getFullYear();
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                setForm((prev: UpdatePolicyPayload) => ({
                                                    ...prev,
                                                    expiryDate: `${year}-${month}-${day}`
                                                }));
                                            }
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </div>

<DialogFooter className="px-6 pb-6 pt-0">
    <Button
        className="px-4 py-2"
        disabled={isPending}
        onClick={handleClose}
        variant="outline"
    >
        Cancel
    </Button>
    <Button
        className="px-4 py-2 bg-primary hover:bg-primary/90"
        disabled={isPending || !policy?.id}
        onClick={handleSave}
    >
        {isPending ? "Saving..." : "Save"}
    </Button>
</DialogFooter>
           </ResponsiveDialogContent>
        </Dialog>
    );
};

export default UpdateLeadPolicy;